<script>
/** @typedef {import('../claims/ProjectViewClaimShow.vue').FileUpload} Invoice */
export default {
  name: "ClaimsSectionInvoice",
  inheritAttrs: false,
  props: {
    parentKey: {
      type: String,
      required: true
    },
    fileKey: {
      type: String,
      required: true
    },
    invoice: {
      type: Object,
      required: false,
      default: null
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      error: null,
      processing: false,
      progress: 0
    };
  },
  methods: {
    /**
     * @description Handle file upload and emit the selected file to the parent component.
     * @param {Event} event - The file input change event.
     * @returns {void}
     */
    handleFileUpload(event) {
      if (this.processing || this.disabled) return;

      this.progress = 0;
      this.processing = true;

      const file = event.target.files;
      if (!file) return this.$emit('upload', null);

			let formData = new FormData();

			formData.append('parent_key', this.parentKey)
			formData.append('key', this.fileKey)
      formData.append(`${this.fileKey}[0]`, file[0])

      axios.post(route('applications.upload', [this.$page.project.uuid]), formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          this.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
      }).then(({ data }) => {
        this.$emit('upload', data[0] || null)
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('File upload failed:', error);
        this.$emit('upload', null);
      })
      .finally(() => {
        this.processing = false;
        this.progress = 0;
      });
    },

    /**
     * @description Remove the currently uploaded invoice and emit null to the parent component.
     * @returns {void}
     */
    removeInvoice() {
      // If we are trying to remove an invoice that doesn't belong to the current claim,
      // we should not proceed with the removal, but remove it from the application state instead.
      // This keeps the history of the claim intact and prevents accidental deletion of unrelated invoices.
      if(this.parentKey !== this.invoice.parent_key) {
        this.$emit('upload', null);
        return;
      }

      this.processing = true;
      const original = {...this.invoice};
      this.$emit('upload', null);
      if(!this.invoice || !this.invoice.id) {
        this.processing = false;
        return;
      }
      axios.delete(route('applications.files.delete', [this.$page.project.uuid, this.invoice.id]))
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to remove invoice:', error);
          this.$emit('upload', original);
        })
        .finally(() => {
          this.processing = false;
          this.progress = 0;
        });
    }
  },
};
</script>

<template>
  <div
    :class="$attrs.class"
  >
    <label
      v-if="!invoice"
      class="action action--small rounded-full"
    >
      <span class="relative z-10">{{ processing ? 'Uploading ' + progress + '%' : 'Upload' }}</span>
      <input
        :id="$attrs.id"
        type="file"
        class="sr-only"
        :disabled="processing || disabled"
        @change="handleFileUpload"
      />
      <span
        v-if="processing"
        class="progress"
        :style="{ width: progress + '%' }"
      />
    </label>
    <div
      v-else
      class="flex w-full items-center justify-between"
    >
      <a
        :href="route('applications.files.download', [this.$page.project.uuid, invoice.id]) + '?project=1'"
        download
        class="text-sm text-gray-700 truncate w-full"
      >
        {{ invoice.name }}
      </a>
      <button
        v-if="!disabled"
        class="action action--small action--danger rounded-full ml-6 flex items-center justify-center text-lg font-bold flex-no-shrink"
        style="width: 1.25rem; height: 1.25rem;"
        @click="removeInvoice"
      >
        <span>&times;</span>
      </button>
    </div>
  </div>
</template>

<style lang="styl" scoped>
.action
  @apply relative overflow-hidden;

  &:has(input:disabled)
    @apply opacity-50 cursor-not-allowed;

.progress
  @apply absolute bg-primary-200;
  transition width 0.3s ease-in-out
  left 0
  top 0
  height 100%

</style>
